import React, { useMemo } from 'react';
import { FormGroup, Row, Col, Accordion, Card, Button } from 'react-bootstrap';
import { isArray, last } from 'lodash';
import { useTranslation } from 'react-i18next';
import { MentionsInput, Mention } from 'react-mentions';
import Select from 'react-select';
import Loader from '../../../../../Components/Utility/Loader/Loader';
import { calculationBtnArrays, customStyles, ColumnValues, customStylesForOCP } from '../../../../../Components/helper';
import { ChevronIcon, ClearIcon, CloseIcon, EditIcon, RedoIcon, SaveIcon, SearchIcon, UndoIcon } from '../../../../../bombiix-icon';
import { NewInput } from '../../../../../Components/PIM/common/NewInput';
import { NewTextArea } from '../../../../../Components/PIM/common/NewTextArea';

export default function TableCalculation({
  pageRef,
  controlRef,
  columnIndex,
  relation,
  onLocationChange,
  redoFormula,
  undoFormula,
  clearFormula,
  clearLocation,
  handleFilter,
  locationRef,
  subLocationRef,
  hasError,
  handleFormulaDirectly,
  allowEditFormula,
  childOptions,
  handleEditClick,
  formulaHasError,
  hideCustomModule,
  rootAtLast,
  colourArray,
  onChange,
}) {
  const { t } = useTranslation('common');

  const customLibraryCase = (val) => {
    return val?.control?.type === 'Table' ? val?.column?.type === 'string' : val?.control?.type === 'string';
  };

  const foundString = relation?.formulaArray?.filter((val) =>
    relation?.currLocation?.name === 'Custom_Modules' ? customLibraryCase(val) : val?.subLocation?.type === 'string',
  );
  let hideBtn = false;
  const checkFormulaHasStringType = foundString?.length > 0;
  const counterState = relation?.counter === 0;

  const checkFormulaHasPercentage = () => {
    if (isArray(relation?.formulaArray)) {
      hideBtn = relation?.formulaArray?.some((val) => val?.location?.name === 'Supplier_Template');
    }
    const lastObj = last(relation?.formulaArray);
    return lastObj?.subLocation?.name === '%';
  };
  const checkFormulaHasRoot = () => {
    const lastObj = last(relation?.formulaArray);
    return lastObj?.subLocation?.name === '√';
  };
  const checkSqAtLast = () => {
    const lastObj = last(relation?.formulaArray);
    return lastObj?.subLocation?.name === 'X²';
  };
  const checkDisableNumbers = () => {
    const lastObj = last(relation?.formulaArray);
    return ['X²', 'π', ')'].includes(lastObj?.subLocation?.name);
  };

  const checkPiAtLast = () => {
    const lastObj = last(relation?.formulaArray);
    return lastObj?.subLocation?.name === 'π';
  };

  const checkDisableSpecials = () => {
    const lastObj = last(relation?.formulaArray);
    return lastObj?.subLocation?.type === 'Number';
  };

  const checkFormulaHasPercentageAtLast = useMemo(() => {
    return {
      percentage: checkFormulaHasPercentage(),
      root: checkFormulaHasRoot(),
      disableNumbers: checkDisableNumbers(),
      disableSpecialCharacter: checkDisableSpecials(),
      squareAtLast: checkSqAtLast(),
      piAtLast: checkPiAtLast(),
      closingBreak: !relation?.bracketCounter,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relation?.formulaArray?.length]);

  const calculationBtnArr = calculationBtnArrays(
    checkFormulaHasStringType,
    counterState,
    checkFormulaHasPercentageAtLast.percentage,
    checkFormulaHasPercentageAtLast.root,
    checkFormulaHasPercentageAtLast.disableNumbers,
    checkFormulaHasPercentageAtLast.disableSpecialCharacter,
    checkFormulaHasPercentageAtLast.squareAtLast,
    checkFormulaHasPercentageAtLast.piAtLast,
    checkFormulaHasPercentageAtLast.closingBreak,
  );

  const fadingStyle = () => {
    return !counterState && !['√', 'X²', 'π'].includes(last(relation?.formulaArray)?.subLocation?.name) ? 'disabled' : '';
  };
  const staicColourArray = [
    {
      colourValue: '',
      colourType: 'green',
      startPercentage: '',
      endPercentage: '',
    },
    {
      colourValue: '',
      colourType: 'amber',
      startPercentage: '',
      endPercentage: '',
    },
    {
      colourValue: '',
      colourType: 'red',
      startPercentage: '',
      endPercentage: '',
    },
  ];
  const colorArrayCopy = colourArray?.length > 0 ? colourArray : staicColourArray;
  const chcekingFromDynamicArray = (colourArrayParams, value) => {
    return typeof value === 'object' ? colourArrayParams?.find((ele) => ele?._id === value?._id) : colourArrayParams?.find((ele) => ele?._id === value);
  };
  return relation?.mainLoader ? (
    <Loader />
  ) : (
    <div key={`calculation_Table_Column_${columnIndex}`} className="calculation-modal">
      <FormGroup className="mb-1 formula-expression-section mt-16">
        <div className="card common-listing-card common-calculation-card">
          <div className="card-header py-2 px-3">{t('CUSTOM_LIBRARY.CALCULATION_TYPE.FORMULA_EXPRESSION')}</div>
          <div className="card-body p-2 d-flex position-relative">
            {allowEditFormula ? (
              <MentionsInput
                value={relation?.readOnlyFormulaString}
                onChange={(e) => handleFormulaDirectly(e?.target?.value)}
                placeholder={t('CUSTOM_MODULES.LIBRARY.TEMPLATE_COLUMN.NEW_CONTROL.CALCULATION_COMPONENET.READONLY_FORMULA_PLACEHOLDER')}
                className="form-control"
              >
                <Mention
                  markup="[__display__]"
                  trigger="@"
                  data={childOptions}
                  displayTransform={(id, display) => display.split('_(')[0]}
                  renderSuggestion={(suggestion, search, highlightedDisplay) => <div className="user mention-user-list">{highlightedDisplay}</div>}
                />
              </MentionsInput>
            ) : (
              <NewTextArea
                name=""
                placeholder={t('CUSTOM_MODULES.LIBRARY.TABLE.TABLE_CALCULATION.CREATED_FORMULA_EXPRESSION')}
                value={relation?.readOnlyFormulaString}
                readOnly={!allowEditFormula}
              />
            )}
            {!hideCustomModule && !hideBtn && (
              <button type="button" className="ml-auto btn p-0 position-absolute placement-top-right" onClick={handleEditClick} aria-label="Edit Formula">
                {allowEditFormula ? <SaveIcon size={24} /> : <EditIcon size={24} />}
              </button>
            )}
          </div>
        </div>
        {formulaHasError && <span className="error-message text-center">{t('CUSTOM_LIBRARY.CALCULATION_TYPE.INVALID_FORMULA')}</span>}
        {rootAtLast && <span className="error-message text-center">{t('CUSTOM_LIBRARY.CALCULATION_TYPE.ROOT_AT_LAST')}</span>}
        {hasError && !relation?.formulaArray?.length && !formulaHasError && (
          <span className="error-message text-center">{t('CUSTOM_LIBRARY.CALCULATION_TYPE.COLUMN_FORMULA_MISSING')}</span>
        )}
      </FormGroup>
      <div className="d-flex formula-btns">
        <button
          type="button"
          className="btn btn-outline"
          onClick={clearFormula}
          disabled={allowEditFormula}
          aria-label={t('CUSTOM_LIBRARY.CALCULATION_TYPE.CLEAR')}
        >
          <ClearIcon size={24} />
        </button>
        {calculationBtnArr?.map((sym) => {
          return (
            <button
              key={sym}
              className={`btn btn-outline ${sym.className}`}
              onClick={() => {
                onLocationChange(sym, 'calcSymbol', columnIndex);
              }}
              type="button"
              disabled={(sym.type !== 'number' && sym?.disabled) || allowEditFormula}
            >
              {sym?.name}
            </button>
          );
        })}
        <button
          type="button"
          className="btn btn-default mw-auto px-2 mr-1 w-50px"
          onClick={undoFormula}
          disabled={(isArray(relation?.undo) && relation?.undo?.length <= 0) || allowEditFormula}
          aria-label="undo"
        >
          <UndoIcon size={24} />
        </button>
        <button
          type="button"
          className="btn btn-default mw-auto px-2 w-50px"
          onClick={redoFormula}
          disabled={(isArray(relation?.redo) && !relation?.redo?.length > 0) || allowEditFormula}
          aria-label="Redo"
        >
          <RedoIcon size={24} />
        </button>
      </div>
      <Row>
        <Col>
          <div className="card common-listing-card common-calculation-card ">
            <div className="card-header f-16  d-flex align-items-center ">{t('CUSTOM_LIBRARY.SOURCE_TYPE.LOCATION')}</div>
            <div className="card-body p-0">
              <div className="calc-filter-container d-flex align-items-center">
                <SearchIcon size={24} />
                <NewInput
                  type="text"
                  className="border-none"
                  ref={locationRef}
                  onChange={(e) => handleFilter(e.target.value, 'location')}
                  placeholder={t('COMMON_LABEL.SEARCH')}
                  disabled={allowEditFormula || checkFormulaHasPercentageAtLast.disableNumbers}
                />
                <button
                  type="button"
                  onClick={() => clearLocation('location')}
                  aria-label="Clear Location"
                  disabled={allowEditFormula || checkFormulaHasPercentageAtLast.disableNumbers}
                  className="btn p-0 border-none bg-transparent"
                >
                  <CloseIcon size={24} />
                </button>
              </div>

              <ul className="location-ul">
                {relation?.filteredLocationArray?.map((loca) => {
                  return (
                    <li key={loca?._id} className={relation?.currLocation?.name === loca?.name && counterState ? 'active' : fadingStyle()}>
                      <button
                        type="button"
                        className="btn p-0"
                        onClick={() => onLocationChange(loca, 'location', columnIndex)}
                        disabled={allowEditFormula || checkFormulaHasPercentageAtLast.disableNumbers}
                      >
                        {loca?.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Col>
        <Col>
          <div className="card common-listing-card common-calculation-card">
            <div className="card-header py-1 f-16  d-flex align-items-center ">{t('CUSTOM_LIBRARY.SOURCE_TYPE.SUB_LOCATION')}</div>
            <div className="card-body p-0">
              <div className="calc-filter-container d-flex align-items-center">
                <SearchIcon size={24} />
                <NewInput
                  type="text"
                  className="border-none"
                  ref={subLocationRef}
                  onChange={(e) => handleFilter(e.target.value, 'subLocation')}
                  placeholder={t('COMMON_LABEL.SEARCH')}
                  disabled={allowEditFormula || checkFormulaHasPercentageAtLast.disableNumbers}
                />
                <button
                  type="button"
                  onClick={() => clearLocation('subLocation')}
                  aria-label="Clear SubLocation"
                  disabled={allowEditFormula || checkFormulaHasPercentageAtLast.disableNumbers}
                  className="btn p-0 border-none bg-transparent"
                >
                  <CloseIcon size={24} />
                </button>
              </div>
              <ul className="location-ul">
                {relation?.filteredSubLocationArray?.map((subLoc) => {
                  return (
                    <li
                      key={subLoc?._id}
                      disabled={!counterState}
                      className={relation?.currSubLocation?.name === subLoc?.name && counterState ? 'active' : fadingStyle()}
                    >
                      <button
                        type="button"
                        className="btn p-0"
                        onClick={() => onLocationChange(subLoc, 'subLocation', columnIndex)}
                        disabled={allowEditFormula || checkFormulaHasPercentageAtLast.disableNumbers}
                      >
                        {subLoc?.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Col>
        {relation?.currLocation?.name === 'Custom_Modules' && relation?.currSubLocation && (
          <Col>
            <div className="card common-listing-card common-calculation-card">
              <div className="card-header py-1 f-16  d-flex align-items-center ">
                {t('CUSTOM_MODULES.LIBRARY.TEMPLATE_COLUMN.NEW_CONTROL.CALCULATION_COMPONENET.PAGE')}
              </div>
              <div className="card-body p-0">
                <div className="calc-filter-container d-flex align-items-center ">
                  <SearchIcon size={24} />
                  <NewInput
                    type="text"
                    className="border-none"
                    ref={pageRef}
                    disabled={allowEditFormula || checkFormulaHasPercentageAtLast.disableNumbers}
                    onChange={(e) => handleFilter(e.target.value, 'page')}
                    placeholder={t('COMMON_LABEL.SEARCH')}
                  />
                  <button
                    type="button"
                    onClick={() => clearLocation('page')}
                    aria-label="Clear Location"
                    disabled={allowEditFormula || checkFormulaHasPercentageAtLast.disableNumbers}
                    className="btn p-0 border-none bg-transparent"
                  >
                    <CloseIcon size={24} />
                  </button>
                </div>

                <ul className="location-ul">
                  {relation?.filteredPageArray?.map((loca) => {
                    return (
                      <li key={loca?.name} className={relation?.currPage?.name === loca?.name && counterState ? 'active' : fadingStyle()}>
                        <button
                          type="button"
                          className="btn p-0"
                          onClick={() => onLocationChange(loca, 'page', columnIndex)}
                          disabled={allowEditFormula || checkFormulaHasPercentageAtLast.disableNumbers}
                        >
                          {loca?.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </Col>
        )}
        {relation?.currLocation?.name === 'Custom_Modules' && relation?.currSubLocation && relation?.currPage && (
          <Col>
            <div className="card common-listing-card common-calculation-card ">
              <div className="card-header py-1 f-16  d-flex align-items-center ">{t('CUSTOM_LIBRARY.SOURCE_TYPE.CONTROLS')}</div>
              <div className="card-body p-0">
                <div className="calc-filter-container d-flex align-items-center">
                  <SearchIcon size={24} />
                  <NewInput
                    type="text"
                    className="border-none"
                    ref={controlRef}
                    disabled={allowEditFormula || checkFormulaHasPercentageAtLast.disableNumbers}
                    onChange={(e) => handleFilter(e.target.value, 'control')}
                    placeholder={t('COMMON_LABEL.SEARCH')}
                  />
                  <button
                    type="button"
                    onClick={() => clearLocation('control')}
                    aria-label="Clear SubLocation"
                    disabled={allowEditFormula || checkFormulaHasPercentageAtLast.disableNumbers}
                    className="btn p-0 border-none bg-transparent"
                  >
                    <CloseIcon size={24} />
                  </button>
                </div>
                <ul className="location-ul">
                  {relation?.filteredControlsArray?.map((ctrl) => {
                    return (
                      <li key={ctrl?.name} className={relation?.currControl?.name === ctrl?.name && counterState ? 'active' : fadingStyle()}>
                        <button
                          type="button"
                          className="btn p-0"
                          onClick={() => onLocationChange(ctrl, 'control', columnIndex)}
                          disabled={!counterState || allowEditFormula || checkFormulaHasPercentageAtLast.disableNumbers}
                        >
                          {ctrl?.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </Col>
        )}
        {relation?.currLocation?.name === 'Custom_Modules' && relation?.currSubLocation && relation?.currPage && relation?.currControl?.type === 'Table' && (
          <Col>
            <div className="card common-listing-card common-calculation-card ">
              <div className="card-header py-1 f-16  d-flex align-items-center ">{t('CUSTOM_LIBRARY.SOURCE_TYPE.TABLE')}</div>
              <div className="card-body p-0">
                <Select
                  className="common-react-select"
                  options={relation?.currControl?.tableColumns}
                  menuPlacement="top"
                  getOptionLabel={(option) => `${option.name}`}
                  getOptionValue={(option) => `${option.key}`}
                  name="Column"
                  value={relation?.currentColumn}
                  styles={customStyles}
                  isDisabled={!counterState || allowEditFormula || checkFormulaHasPercentageAtLast.disableNumbers}
                  placeholder={t('CUSTOM_LIBRARY.SOURCE_TYPE.TABLE_COLUMN_PLACEHOLDER')}
                  onChange={(e) => onLocationChange(e, 'currentColumn', columnIndex)}
                />
              </div>
            </div>
          </Col>
        )}
      </Row>
      <Row>
        <Col md={12}>
          <div className="collapse-card">
            <Accordion defaultActiveKey="0">
              <Card className="mt-16">
                <Card.Header className="calculation-color-header">
                  {t('CUSTOM_MODULE.COLOR_CODING')}
                  <Accordion.Toggle className="btn-toggle d-flex" as={Button} variant="link" eventKey="0">
                    <ChevronIcon size={24} />
                  </Accordion.Toggle>
                </Card.Header>
                <Accordion.Collapse eventKey="0">
                  <Card.Body>
                    <div className="flex-fill">
                      {colorArrayCopy?.map((element, index) => {
                        return (
                          <div className="d-flex align-items-center f-14 mb-2" key={element}>
                            <div className="status-color-container style-two d-flex">
                              <span className={`${element?.colourType} rount-shape`} />
                            </div>
                            <div className="d-flex flex-fill flex-wrap align-items-center">
                              <span className="pl-16 text-nowrap">if</span>
                              <div className="flex-fill px-2 my-1 my-md-0">
                                <Select
                                  name="colourValue"
                                  options={ColumnValues?.filter((ele) => !colorArrayCopy?.map((e) => e?.colourValue)?.includes(ele?._id))}
                                  getOptionLabel={(option) => `${option.name}`}
                                  getOptionValue={(option) => `${option._id}`}
                                  menuPlacement="bottom"
                                  menuPosition="fixed"
                                  isClearable
                                  menuShouldBlockScroll
                                  menuShouldScrollIntoView
                                  value={chcekingFromDynamicArray(ColumnValues, element?.colourValue) || null}
                                  styles={customStylesForOCP}
                                  maxMenuHeight={140}
                                  onChange={(e) => onChange(e, columnIndex, 'colourValue', index)}
                                  placeholder="Select Value "
                                />
                              </div>
                              <NewInput
                                className="text-bordered percentaage-input ml-16"
                                onChange={(e) => onChange(e?.target?.value, columnIndex, 'startPercentage', index)}
                                disabled={!element?.colourValue}
                                value={element?.startPercentage}
                                name={`colourStateValue_start_${index}`}
                                type="number"
                              />{' '}
                              {element?.colourValue === 'between' && (
                                <div className="d-flex align-items-center ml-16">
                                  {' and '}
                                  <NewInput
                                    className="text-bordered percentaage-input ml-16"
                                    onChange={(e) => onChange(e?.target?.value, columnIndex, 'endPercentage', index)}
                                    disabled={!element?.colourValue}
                                    value={element?.endPercentage}
                                    name={`colourStateValue_end_${index}`}
                                    type="number"
                                  />{' '}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          </div>
        </Col>
      </Row>
    </div>
  );
}
